import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Calendar, Smartphone, Monitor, ChevronRight } from 'lucide-react';

interface CalendarImportGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName?: string;
}

const CalendarImportGuideModal: React.FC<CalendarImportGuideModalProps> = ({
  isOpen,
  onClose,
  fileName = 'milky-way-calendar.ics'
}) => {
  const handleOpenFile = () => {
    // This will trigger the browser's file association for .ics files
    window.open(`data:text/calendar;charset=utf-8,`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Import Your Milky Way Calendar
          </DialogTitle>
          <DialogDescription>
            Follow these step-by-step instructions to import your calendar file into your preferred calendar application.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Download className="h-4 w-4" />
                Downloaded: {fileName}
              </CardTitle>
              <CardDescription>
                Your calendar file has been downloaded. Use the instructions below to import it.
              </CardDescription>
            </CardHeader>
          </Card>

          <Tabs defaultValue="google" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="google" className="flex items-center gap-2">
                <Monitor className="h-4 w-4" />
                Google Calendar
              </TabsTrigger>
              <TabsTrigger value="apple" className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Apple Calendar
              </TabsTrigger>
              <TabsTrigger value="outlook" className="flex items-center gap-2">
                <Monitor className="h-4 w-4" />
                Outlook
              </TabsTrigger>
            </TabsList>

            <TabsContent value="google" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Google Calendar Import</CardTitle>
                  <CardDescription>
                    Import your Milky Way photography calendar into Google Calendar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Open Google Calendar</p>
                        <p className="text-sm text-muted-foreground">
                          Go to <code className="bg-muted px-1 rounded">calendar.google.com</code> in your browser
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Access Import Settings</p>
                        <p className="text-sm text-muted-foreground">
                          Click the gear icon (⚙️) in the top right, then select "Settings"
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Import Calendar</p>
                        <p className="text-sm text-muted-foreground">
                          In the left sidebar, click "Import & export" → "Import" → "Select file from your computer"
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        4
                      </div>
                      <div>
                        <p className="font-medium">Upload Your File</p>
                        <p className="text-sm text-muted-foreground">
                          Select the downloaded <code className="bg-muted px-1 rounded">{fileName}</code> file and click "Import"
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium">💡 Pro Tip</p>
                    <p className="text-sm text-muted-foreground">
                      Your Milky Way events will appear as all-day events. You can create a separate calendar 
                      for photography planning to keep them organized.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="apple" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Apple Calendar Import</CardTitle>
                  <CardDescription>
                    Import your calendar on macOS, iOS, or iPadOS
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        macOS (Desktop)
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Double-click the downloaded .ics file</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Calendar app will open automatically</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Choose which calendar to add events to</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Click "Import" to confirm</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        iOS/iPadOS (Mobile)
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Email the .ics file to yourself</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Open the email on your device</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Tap the .ics attachment</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          <span>Select "Add All" to import events</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium">📱 Alternative Method</p>
                    <p className="text-sm text-muted-foreground">
                      You can also save the file to iCloud Drive and open it from the Files app, 
                      then tap to import into Calendar.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="outlook" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Microsoft Outlook Import</CardTitle>
                  <CardDescription>
                    Import your calendar in Outlook (web, desktop, or mobile)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Open Outlook</p>
                        <p className="text-sm text-muted-foreground">
                          Use Outlook on the web (<code className="bg-muted px-1 rounded">outlook.live.com</code>) or desktop app
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Navigate to Calendar</p>
                        <p className="text-sm text-muted-foreground">
                          Click on the Calendar icon in the left navigation or press Ctrl+2 (desktop)
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Import Calendar</p>
                        <p className="text-sm text-muted-foreground">
                          Click "Add calendar" → "Upload from file" → Choose your .ics file
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        4
                      </div>
                      <div>
                        <p className="font-medium">Complete Import</p>
                        <p className="text-sm text-muted-foreground">
                          Give your calendar a name (e.g., "Milky Way Photography") and click "Import"
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium">🔄 Desktop Alternative</p>
                    <p className="text-sm text-muted-foreground">
                      In Outlook desktop: File → Open & Export → Import/Export → 
                      Import an iCalendar (.ics) or vCalendar file
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarImportGuideModal;